'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { gsap } from 'gsap';
import { getPageBackgroundColor } from '@/lib/pageColors';

/**
 * Darkens a hex color by a percentage
 * @param hex - Hex color code (e.g., '#FFDC32')
 * @param percent - Percentage to darken (0-100)
 * @returns Darkened hex color
 */
function darkenColor(hex: string, percent: number): string {
  // Remove the hash if present
  hex = hex.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate darkened values
  const darkenAmount = percent / 100;
  const newR = Math.round(r * (1 - darkenAmount));
  const newG = Math.round(g * (1 - darkenAmount));
  const newB = Math.round(b * (1 - darkenAmount));
  
  // Convert back to hex
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

export default function PageTransition() {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionColor, setTransitionColor] = useState('#FDFEF6');
  const containerRef = useRef<HTMLDivElement>(null);
  const topPanelRef = useRef<HTMLDivElement>(null);
  const bottomPanelRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const prevPathnameRef = useRef(pathname);
  const isAnimatingOutRef = useRef(false);
  const pendingHrefRef = useRef<string | null>(null);

  // Intercept link clicks
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (!link) return;
      
      const href = link.getAttribute('href');
      if (!href) return;
      
      // Skip external links, mailto, tel, etc.
      if (href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) {
        return;
      }
      
      // Skip if clicking the same page
      if (href === pathname) return;
      
      // Skip links inside the AI contact modal
      const contactModal = link.closest('.contact-modal, #contact-modal');
      if (contactModal) {
        return;
      }
      
      // Prevent default navigation temporarily
      e.preventDefault();
      
      // Store the href to navigate to
      pendingHrefRef.current = href;
      
      // Get the background color for the destination page and darken it for the transition panels
      const destinationColor = getPageBackgroundColor(href);
      const darkenedColor = darkenColor(destinationColor, 5); // Darken by 5%
      setTransitionColor(darkenedColor);
      
      // Start transition
      setIsTransitioning(true);
      isAnimatingOutRef.current = false;
      
      // console.log('PageTransition: Starting transition to', href);
      
      // Wait for next frame to ensure state is set and panels are rendered
      requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight;

        if (!topPanelRef.current || !bottomPanelRef.current) {
          // console.log('PageTransition: Refs not ready');
          return;
        }

        // Set initial state - panels start off screen (each panel is 100vh, so move by viewport height)
        gsap.set(topPanelRef.current, { y: -viewportHeight });
        gsap.set(bottomPanelRef.current, { y: viewportHeight });

        // Animate panels in to cover the page (ease-in for opening)
        gsap.to(topPanelRef.current, {
          y: 0,
          duration: 0.8, // Slower duration
          ease: 'power4.in'
        });

        gsap.to(bottomPanelRef.current, {
          y: 0,
          duration: 0.8, // Slower duration
          ease: 'power4.in',
          onComplete: () => {
            // console.log('PageTransition: Panels covered, navigating to', pendingHrefRef.current);
            // Navigate after panels cover the page
            if (pendingHrefRef.current) {
              router.push(pendingHrefRef.current);
              pendingHrefRef.current = null;
            }
          }
        });
      });
    };

    document.addEventListener('click', handleLinkClick, true);
    
    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [pathname, router]);

  // Handle pathname change (when new page loads)
  useEffect(() => {
    if (pathname !== prevPathnameRef.current && isTransitioning && !isAnimatingOutRef.current) {
      // console.log('PageTransition: Pathname changed to', pathname, '- animating panels out');
      prevPathnameRef.current = pathname;
      isAnimatingOutRef.current = true;
      
      // Animate panels out immediately (no delay for subsequent transitions)
      requestAnimationFrame(() => {
        if (!topPanelRef.current || !bottomPanelRef.current) {
          // console.log('PageTransition: Refs not ready for exit animation');
          return;
        }

        // Get the actual computed height of the panels (which use 100dvh) to match CSS
        // This ensures we use the same height value as the CSS, accounting for mobile browser UI
        const viewportHeight = topPanelRef.current.offsetHeight || window.innerHeight;

        // console.log('PageTransition: Starting exit animation');

        // Animate panels out to reveal new page (ease-out for closing)
        gsap.to(topPanelRef.current, {
          y: -viewportHeight,
          duration: 1, // Slower duration
          ease: 'power4.inOut'
        });

        gsap.to(bottomPanelRef.current, {
          y: viewportHeight,
          duration: 1, // Slower duration
          ease: 'power4.inOut',
          onComplete: () => {
            // console.log('PageTransition: Exit animation complete');
            setIsTransitioning(false);
            isAnimatingOutRef.current = false;
          }
        });
      });
    }
  }, [pathname, isTransitioning]);

  if (!isTransitioning) return null;

  return (
    <div 
      ref={containerRef}
      className="fixed pointer-events-none overflow-hidden" 
      style={{ 
        zIndex: 99999,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100dvh'
      }}
    >
      {/* Top Panel - covers top half and extends beyond */}
      <div
        ref={topPanelRef}
        className="absolute left-0 w-full"
        style={{ 
          top: 0,
          height: '100dvh', 
          zIndex: 100000,
          backgroundColor: transitionColor
        }}
      />

      {/* Bottom Panel - covers bottom half and extends beyond */}
      <div
        ref={bottomPanelRef}
        className="absolute left-0 w-full"
        style={{ 
          bottom: 0,
          height: '100dvh', 
          zIndex: 100000,
          backgroundColor: transitionColor
        }}
      />
    </div>
  );
}

